import React, { useState, useEffect, useRef } from 'react';
import { getWebSocketBaseUrl } from '../utils/api';
import { useQuery } from '@tanstack/react-query';
import { Reports } from './Reports.jsx';


export function LiveEventView({ api, eventStateId }) {
    const [eventStatus, setEventStatus] = useState(null);
    const [selectedClassId, setSelectedClassId] = useState(null);
    const [leaderboards, setLeaderboards] = useState(new Map()); // Store leaderboards for all classes
    const [error, setError] = useState(null);
    const [connected, setConnected] = useState(false);
    const [wsError, setWsError] = useState(null);
    const [wsConnecting, setWsConnecting] = useState(false);

    // Separate refs for event state and class WebSockets
    const eventWsRef = useRef(null);
    const classWsRefs = useRef(new Map()); // Map to store WebSocket refs for each class
    const retryTimeoutRef = useRef(null);
    const retryCountRef = useRef(0);
    const maxRetries = 3;

    // Function to connect to class WebSocket
    const connectToClass = async (classId) => {
        if (!classId) return;

        // If we already have a connection for this class, don't create a new one
        if (classWsRefs.current.has(classId)) {
            return;
        }

        // Fetch initial leaderboard
        try {
            const initialLeaderboard = await api.getClassLeaderboard(classId);
            setLeaderboards(prev => new Map(prev).set(classId, initialLeaderboard));
        } catch (err) {
            console.error('Failed to fetch initial leaderboard:', err);
            setLeaderboards(prev => new Map(prev).set(classId, []));
        }

        const baseUrl = getWebSocketBaseUrl();
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsUrl = baseUrl ? `${baseUrl}/api/classes/${classId}` : `${protocol}//${window.location.host}/api/classes/${classId}`;
        const ws = new WebSocket(wsUrl);
        classWsRefs.current.set(classId, ws);

        ws.onopen = () => {
            console.log(`Class WebSocket connected for class ${classId}`);
        };

        ws.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                if (message.type === 'leaderboard') {
                    setLeaderboards(prev => new Map(prev).set(classId, message.data));
                }
            } catch (err) {
                console.error('Failed to parse class WebSocket message:', err);
            }
        };

        ws.onclose = () => {
            // Remove the WebSocket reference when closed
            classWsRefs.current.delete(classId);
            // Try to reconnect if the class is still selected
            if (selectedClassId === classId) {
                setTimeout(() => connectToClass(classId), 2000);
            }
        };

        ws.onerror = (err) => {
            console.error(`Class WebSocket error for class ${classId}:`, err);
        };
    };

    // Handle class selection
    const handleClassSelect = (classId) => {
        setSelectedClassId(classId);
        connectToClass(classId);
    };

    // Clean up function for class WebSockets
    const cleanupClassWebSockets = () => {
        classWsRefs.current.forEach((ws, classId) => {
            ws.close();
            classWsRefs.current.delete(classId);
        });
    };

    // WebSocket connection for event state updates
    useEffect(() => {
        if (!eventStateId) return;

        const connectWebSocket = () => {
            const baseUrl = getWebSocketBaseUrl();
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            const wsUrl = baseUrl ? `${baseUrl}/api/event-state/${eventStateId}` : `${protocol}//${window.location.host}/api/event-state/${eventStateId}`;

            // Close existing connection if any
            if (eventWsRef.current) {
                eventWsRef.current.close();
            }

            setWsConnecting(true);
            setWsError(null);

            const ws = new WebSocket(wsUrl);
            eventWsRef.current = ws;

            ws.onopen = () => {
                setConnected(true);
                setWsConnecting(false);
                setWsError(null);
                retryCountRef.current = 0; // Reset retry count on successful connection
            };

            ws.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    if (message.type === 'status') {
                        const newStatus = message.data;
                        setEventStatus(newStatus);

                        // Connect to new class WebSocket when class changes
                        if (newStatus.currentClass?.classId) {
                            connectToClass(newStatus.currentClass.classId);
                            // If no class is selected, select the current class
                            if (!selectedClassId) {
                                setSelectedClassId(newStatus.currentClass.classId);
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error parsing WebSocket message:', error);
                }
            };

            ws.onclose = (event) => {
                setConnected(false);
                setWsConnecting(false);

                // Only retry if it's not a normal closure and we still have an eventStateId
                if (event.code !== 1000 && eventStateId && retryCountRef.current < maxRetries) {
                    retryCountRef.current++;
                    setWsError(`Connection lost. Retrying... (${retryCountRef.current}/${maxRetries})`);

                    // Clear any existing retry timeout
                    if (retryTimeoutRef.current) {
                        clearTimeout(retryTimeoutRef.current);
                    }

                    // Retry after 2 seconds
                    retryTimeoutRef.current = setTimeout(() => {
                        connectWebSocket();
                    }, 2000);
                } else if (retryCountRef.current >= maxRetries) {
                    setWsError('Failed to connect after multiple attempts. Please check if the event state exists.');
                }
            };

            ws.onerror = (error) => {
                console.error('EventControl WebSocket error:', error);
                setConnected(false);
                setWsConnecting(false);
            };
        };

        // Reset retry count when eventStateId changes
        retryCountRef.current = 0;

        // Initial connection with delay
        const initialDelay = setTimeout(() => {
            connectWebSocket();
        }, 2000);

        return () => {
            // Cleanup function
            clearTimeout(initialDelay);

            // Clear retry timeout
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }

            // Close event WebSocket
            if (eventWsRef.current) {
                eventWsRef.current.close();
                eventWsRef.current = null;
            }

            // Close all class WebSockets
            cleanupClassWebSockets();
        };
    }, [eventStateId]);

    // Fallback: Fetch initial status via HTTP if WebSocket fails
    useEffect(() => {
        if (!eventStateId || eventStatus) return;

        const fetchInitialStatus = async () => {
            try {
                const status = await api.getEventStatus(eventStateId);
                setEventStatus(status);

                // Connect to class WebSocket if there's a current class
                if (status.currentClass?.classId) {
                    connectToClass(status.currentClass.classId);
                }
            } catch (error) {
                console.error('Failed to fetch initial status:', error);
                setError('Failed to fetch event status');
            }
        };

        // Wait a bit for WebSocket to connect, then fallback to HTTP
        const timeoutId = setTimeout(fetchInitialStatus, 3000);

        return () => clearTimeout(timeoutId);
    }, [eventStateId, eventStatus, api]);

    // Calculate runs until next drag
    const calculateRunsUntilDrag = (eventStatus) => {
        if (!eventStatus?.currentClass?.dragsEveryNRuns) return null;
        const dragsEveryNRuns = eventStatus.currentClass.dragsEveryNRuns;
        if (dragsEveryNRuns <= 0) return null;

        const runs = eventStatus.runs || [];
        const currentRunCount = runs.length;
        const runsUntilDrag = dragsEveryNRuns - (currentRunCount % dragsEveryNRuns);
        return runsUntilDrag;
    };

    const renderRun = (run) => {
        if (run.type === 'drag') {
            return (
                <div key={run.id} className="flex items-center justify-center p-4 bg-gray-100 border-b border-gray-200">
                    <div className="flex items-center space-x-2">
                        <span className="text-gray-600">🚜</span>
                        <span className="font-medium text-gray-800">Arena Drag</span>
                    </div>
                </div>
            );
        }

        return (
            <div key={run.id} className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex-1">
                    <div className="font-medium">{run.participantName}</div>
                    <div className="text-sm text-gray-500">{run.horseName}</div>
                </div>
                <div className="text-right">
                    <div className="font-medium">{run.time ? run.time.toFixed(3) : '--'}</div>
                    {run.penalties > 0 && (
                        <div className="text-sm text-red-500">+{run.penalties}</div>
                    )}
                </div>
            </div>
        );
    };

    const renderCurrentParticipant = (participant) => {
        if (!participant) {
            return (
                <div className="text-center py-4 text-muted-foreground">
                    <p>No current participant</p>
                </div>
            );
        }

        if (participant.type === 'drag') {
            return (
                <div className="rounded-lg border-2 border-orange-500 bg-orange-50 p-6 shadow-sm">
                    <div className="text-center">
                        <div className="flex items-center justify-center space-x-2 mb-4">
                            <div className="h-8 w-8 rounded-full bg-orange-500/10 flex items-center justify-center">
                                <span className="text-orange-600 text-lg">🚜</span>
                            </div>
                            <h4 className="text-xl font-bold text-foreground">Arena Drag</h4>
                        </div>
                        <p className="text-lg text-muted-foreground">Preparing arena for next set of runs</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="rounded-lg border-2 border-green-500 bg-green-50 p-6 shadow-sm">
                <div className="text-center">
                    <div className="flex items-center justify-center space-x-2 mb-4">
                        <div className="h-8 w-8 rounded-full bg-green-500/10 flex items-center justify-center">
                            <span className="text-green-600 text-lg">🏇</span>
                        </div>
                        <h4 className="text-xl font-bold text-foreground">Currently Running</h4>
                    </div>
                    <p className="text-2xl font-bold text-green-600 mb-2">{participant.name}</p>
                    {participant.homeTown && (
                        <p className="text-lg text-muted-foreground mb-2">
                            From: {participant.homeTown}
                        </p>
                    )}
                    <p className="text-lg text-muted-foreground">{participant.horseName}</p>
                </div>
            </div>
        );
    };

    if (!eventStatus) {
        return (
            <div className="rounded-lg border bg-card p-6 shadow-sm">
                <div className="flex items-center space-x-2 mb-4">
                    <div className="h-6 w-6 rounded-full bg-blue-500/10 flex items-center justify-center">
                        <span className="text-blue-600 text-sm">📺</span>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">Live Event View</h3>
                </div>
                <div className="flex items-center space-x-2">
                    <div className={`h-3 w-3 rounded-full ${connected ? 'bg-green-500' :
                        wsConnecting ? 'bg-yellow-500 animate-pulse' :
                            'bg-red-500'
                        }`}></div>
                    <p className="text-muted-foreground">
                        {connected ? 'Connected' :
                            wsConnecting ? 'Connecting...' :
                                'Disconnected'}
                    </p>
                </div>
            </div>
        );
    }

    if (eventStatus.status === 'no_event') {
        return (
            <div className="rounded-lg border bg-card p-6 shadow-sm">
                <div className="flex items-center space-x-2 mb-4">
                    <div className="h-6 w-6 rounded-full bg-gray-500/10 flex items-center justify-center">
                        <span className="text-gray-600 text-sm">📺</span>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">Live Event View</h3>
                </div>
                <p className="text-muted-foreground">No event is currently running.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-4">
            {/* Connection Status */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <div className="h-6 w-6 rounded-full bg-blue-500/10 flex items-center justify-center">
                        <span className="text-blue-600 text-sm">📺</span>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">Live Event View</h3>
                </div>
                <div className="flex items-center space-x-2">
                    <div className={`h-3 w-3 rounded-full ${connected ? 'bg-green-500' :
                        wsConnecting ? 'bg-yellow-500 animate-pulse' :
                            'bg-red-500'
                        }`}></div>
                    <span className="text-sm text-muted-foreground">
                        {connected ? 'Live' :
                            wsConnecting ? 'Connecting...' :
                                'Offline'}
                    </span>
                </div>
            </div>

            {/* Error Alert */}
            {error && (
                <div className="rounded-lg border border-orange-500/50 bg-orange-500/10 p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <span className="text-orange-600">⚠️</span>
                            <p className="text-sm text-orange-600">{error}</p>
                        </div>
                        <button
                            onClick={() => {
                                setError(null);
                                retryCountRef.current = 0; // Reset retry count
                                if (eventWsRef.current) {
                                    eventWsRef.current.close();
                                }
                                if (classWsRefs.current.size > 0) {
                                    cleanupClassWebSockets();
                                }
                            }}
                            className="text-xs text-orange-600 hover:text-orange-700 underline"
                        >
                            Retry Now
                        </button>
                    </div>
                </div>
            )}

            {/* Event Status Display */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Event Status */}
                <div className="rounded-lg border bg-card p-4 shadow-sm">
                    <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm text-muted-foreground">Event Status</span>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className={`h-4 w-4 rounded-full ${eventStatus.status === 'running' ? 'bg-green-500' :
                            eventStatus.status === 'stopped' ? 'bg-red-500' :
                                eventStatus.status === 'completed' ? 'bg-blue-500' : 'bg-gray-500'
                            }`}></div>
                        <p className="text-lg font-semibold text-foreground">
                            {eventStatus.status === 'running' ? 'Running' :
                                eventStatus.status === 'stopped' ? 'Stopped' :
                                    eventStatus.status === 'completed' ? 'Completed' : 'Not Started'}
                        </p>
                    </div>
                </div>

                {/* Current Class - Only show if not completed */}
                {eventStatus.status !== 'completed' && (
                    <div className="rounded-lg border bg-card p-4 shadow-sm">
                        <div className="flex items-center space-x-2 mb-2">
                            <span className="text-sm text-muted-foreground">Current Class</span>
                        </div>
                        <p className="text-lg font-semibold text-foreground">
                            {eventStatus.currentClass ? eventStatus.currentClass.name : 'None'}
                        </p>
                    </div>
                )}

                {/* Progress - Only show if not completed */}
                {eventStatus.status !== 'completed' && (
                    <div className="rounded-lg border bg-card p-4 shadow-sm">
                        <div className="flex items-center space-x-2 mb-2">
                            <span className="text-sm text-muted-foreground">Progress</span>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <p className="text-sm font-medium">Classes</p>
                                    <p className="text-sm text-muted-foreground">
                                        {eventStatus.currentClassIndex + 1} of {eventStatus.classes?.length || 0}
                                    </p>
                                </div>
                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-primary transition-all duration-300 rounded-full"
                                        style={{
                                            width: `${((eventStatus.currentClassIndex + 1) / (eventStatus.classes?.length || 1)) * 100}%`
                                        }}
                                    ></div>
                                </div>
                            </div>

                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <p className="text-sm font-medium">Current Class Runs</p>
                                    <p className="text-sm text-muted-foreground">
                                        {eventStatus.currentParticipantIndex + 1} of {eventStatus.currentClass?.participants?.length || 0}
                                    </p>
                                </div>
                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-green-500 transition-all duration-300 rounded-full"
                                        style={{
                                            width: `${((eventStatus.currentParticipantIndex + 1) / (eventStatus.currentClass?.participants?.length || 1)) * 100}%`
                                        }}
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Event Details */}
            <div className="mt-6 rounded-lg border bg-card p-6 shadow-sm">
                <div className="flex items-center space-x-2 mb-4">
                    <div className="h-6 w-6 rounded-full bg-purple-500/10 flex items-center justify-center">
                        <span className="text-purple-600 text-sm">📋</span>
                    </div>
                    <h4 className="text-lg font-semibold text-foreground">Event Details</h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">Event Name</p>
                        <p className="text-lg font-semibold text-foreground">{eventStatus.eventName}</p>
                    </div>
                    <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">Start Time</p>
                        <p className="text-lg font-semibold text-foreground">
                            {eventStatus.startTime ? new Date(eventStatus.startTime).toLocaleString() : 'Not started'}
                        </p>
                    </div>
                    {eventStatus.status === 'completed' && eventStatus.endTime && (
                        <div className="space-y-2">
                            <p className="text-sm text-muted-foreground">End Time</p>
                            <p className="text-lg font-semibold text-foreground">
                                {new Date(eventStatus.endTime).toLocaleString()}
                            </p>
                        </div>
                    )}
                    <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">Last Update</p>
                        <p className="text-lg font-semibold text-foreground">
                            {eventStatus.lastUpdate ? new Date(eventStatus.lastUpdate).toLocaleString() : 'Never'}
                        </p>
                    </div>
                    <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">Total Classes</p>
                        <p className="text-lg font-semibold text-foreground">{eventStatus.classes?.length || 0}</p>
                    </div>
                </div>
            </div>

            {/* Next Up - Only show if not completed */}
            {eventStatus.status !== 'completed' && (
                <div className="rounded-lg border bg-card p-4 shadow-sm">
                    <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm text-muted-foreground">Next Up</span>
                    </div>
                    <p className="text-lg font-semibold text-foreground">
                        {eventStatus.currentClass?.participants?.[eventStatus.currentParticipantIndex + 1]?.name || 'None'}
                    </p>
                </div>
            )}

            {/* In The Hole - Only show if not completed */}
            {eventStatus.status !== 'completed' && (
                <div className="col-span-1 rounded-lg border bg-card p-4 shadow-sm">
                    <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm text-muted-foreground">In The Hole</span>
                    </div>
                    <p className="text-lg font-semibold text-foreground">
                        {eventStatus.currentClass?.participants?.[eventStatus.currentParticipantIndex + 2]?.name || 'None'}
                    </p>
                </div>
            )}

            {/* Current Participant Highlight */}
            {eventStatus.status === 'running' && eventStatus.currentParticipant && (
                renderCurrentParticipant(eventStatus.currentParticipant)
            )}

            {eventStatus.status === 'completed' && (
                <div className="rounded-lg border-2 border-blue-500 bg-blue-50 p-6 shadow-sm">
                    <div className="text-center">
                        <div className="flex items-center justify-center space-x-2 mb-4">
                            <div className="h-8 w-8 rounded-full bg-blue-500/10 flex items-center justify-center">
                                <span className="text-blue-600 text-lg">🏁</span>
                            </div>
                            <h4 className="text-xl font-bold text-foreground">Event Completed!</h4>
                        </div>
                        <p className="text-lg text-blue-600 mb-2">
                            All classes and runs have been finished
                        </p>
                        {eventStatus.endTime && (
                            <p className="text-sm text-muted-foreground">
                                Completed at: {new Date(eventStatus.endTime).toLocaleString()}
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Drag Countdown */}
            {eventStatus?.status === 'running' && (
                <div className="rounded-lg border bg-card p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <span className="text-gray-600">🚜</span>
                            <span className="font-medium">Next Arena Drag</span>
                        </div>
                        {calculateRunsUntilDrag(eventStatus) !== null ? (
                            <div className="flex items-center space-x-2">
                                <span className="text-sm text-muted-foreground">in</span>
                                <span className="font-bold text-lg">{calculateRunsUntilDrag(eventStatus)}</span>
                                <span className="text-sm text-muted-foreground">runs</span>
                            </div>
                        ) : (
                            <span className="text-sm text-muted-foreground">Drags not configured</span>
                        )}
                    </div>
                    {calculateRunsUntilDrag(eventStatus) !== null && (
                        <div className="mt-2">
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-green-500 transition-all duration-300 rounded-full"
                                    style={{
                                        width: `${((eventStatus.currentClass.dragsEveryNRuns - calculateRunsUntilDrag(eventStatus)) / eventStatus.currentClass.dragsEveryNRuns) * 100}%`
                                    }}
                                ></div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Class List with Leaderboard */}
            {eventStatus?.classes && eventStatus.classes.length > 0 && (
                <div className="space-y-6">
                    {/* Class Selection Tabs */}
                    <div className="flex flex-wrap gap-2">
                        {eventStatus.classes.map((classInfo, index) => (
                            <button
                                key={classInfo.classId}
                                onClick={() => handleClassSelect(classInfo.classId)}
                                className={`px-4 py-2 rounded-lg border transition-colors ${selectedClassId === classInfo.classId
                                    ? 'border-primary bg-primary/5 text-primary'
                                    : 'border-gray-200 hover:border-primary/50'
                                    }`}
                            >
                                <span className="font-medium">{classInfo.name}</span>
                                {index === eventStatus.currentClassIndex && (
                                    <span className="ml-2 text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                                        Current
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Leaderboard for Selected Class */}
                    {selectedClassId && (
                        <div className="rounded-lg border bg-card p-6 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center space-x-2">
                                    <div className="h-6 w-6 rounded-full bg-purple-500/10 flex items-center justify-center">
                                        <span className="text-purple-600 text-sm">🏆</span>
                                    </div>
                                    <h4 className="text-lg font-semibold text-foreground">
                                        {eventStatus.classes.find(c => c.classId === selectedClassId)?.name} Standings
                                    </h4>
                                </div>
                            </div>
                            {leaderboards.get(selectedClassId)?.length > 0 ? (
                                <div className="space-y-6">
                                    {leaderboards.get(selectedClassId).map((division) => (
                                        <div key={division.divisionId} className="space-y-2">
                                            <h5 className="text-lg font-semibold flex items-center space-x-2">
                                                <span>{division.divisionId}</span>
                                                <span className="text-sm text-muted-foreground">
                                                    (+{division.timeSplit.toFixed(1)}s)
                                                </span>
                                            </h5>
                                            {division.runs.length > 0 ? (
                                                <div className="space-y-2">
                                                    {division.runs.slice(0, 5).map((entry) => (
                                                        <div
                                                            key={`${division.divisionId}-${entry.participantId}`}
                                                            className={`flex items-center justify-between p-3 rounded-lg ${entry.place === 1 ? 'bg-yellow-50 border border-yellow-200' : 'bg-white border border-gray-200'
                                                                }`}
                                                        >
                                                            <div className="flex items-center space-x-4">
                                                                <span className="font-semibold text-lg w-8">{entry.place}</span>
                                                                <div>
                                                                    <p className="font-medium">{entry.participantName}</p>
                                                                    <p className="text-sm text-muted-foreground">
                                                                        {[entry.homeTown, entry.horseName].filter(Boolean).join(' • ')}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div className="text-right">
                                                                <p className="font-semibold">{entry.finalTime.toFixed(3)}s</p>
                                                                {entry.penalties > 0 && (
                                                                    <p className="text-sm text-red-500">+{entry.penalties}s penalties</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {division.runs.length > 5 && (
                                                        <div className="text-center py-2 text-sm text-muted-foreground">
                                                            <p>+ {division.runs.length - 5} more participants</p>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="text-center py-4 text-muted-foreground text-sm">
                                                    <p>No runs in this division yet</p>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    <p>No runs completed yet</p>
                                    <p className="text-sm">Standings will appear here as participants complete their runs</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Reports */}
            {/* <div className="rounded-lg border bg-card p-6 shadow-sm">
                <div className="flex items-center space-x-2 mb-4">
                    <div className="h-6 w-6 rounded-full bg-blue-500/10 flex items-center justify-center">
                        <span className="text-blue-600 text-sm">📊</span>
                    </div>
                    <h4 className="text-lg font-semibold text-foreground">Reports</h4>
                </div>

                <Reports
                    api={api}
                    eventId={eventStatus?.eventId}
                    classes={eventStatus?.classes || []}
                    eventName={eventStatus?.eventName || 'Event'}
                />
            </div> */}

            {/* Event Webpage */}
            {/* <div className="rounded-lg border bg-card p-6 shadow-sm">
                <div className="flex items-center space-x-2 mb-4">
                    <div className="h-6 w-6 rounded-full bg-green-500/10 flex items-center justify-center">
                        <span className="text-green-600 text-sm">🌐</span>
                    </div>
                    <h4 className="text-lg font-semibold text-foreground">Event Webpage</h4>
                </div>

                <div className="space-y-4">
                    <p className="text-muted-foreground">
                        View a beautiful, printable webpage showing all classes and participants in the correct order.
                        Perfect for event organizers and spectators.
                    </p>

                    <div className="flex flex-wrap gap-3">
                        <a
                            href={`/event/${eventStatus?.eventId}/webpage`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-green-600 text-white hover:bg-green-700 h-10 px-4 py-2"
                        >
                            🌐 View Event Webpage
                        </a>
                    </div>
                </div>
            </div> */}
        </div>
    );
} 